import { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import {
  FETCH_TIMEOUT_ERROR_NAME,
  SW_REQUEST_TIMEOUT_MESSAGE,
} from '@lib/constants/fetch';

export default function getStatusFromError(
  error: ErrorLike,
): ErrorPageStatusEnum {
  const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : [];
  const networkError = CombinedGraphQLErrors.is(error) ? undefined : error;
  if (
    graphQLErrors.some(
      ({ extensions }) => extensions?.code === 'AUTHENTICATION_ERROR',
    )
  ) {
    return ErrorPageStatusEnum.Status401;
  }
  if (
    graphQLErrors.some(
      ({ extensions }) => extensions?.code === 'NOT_FOUND_ERROR',
    )
  ) {
    return ErrorPageStatusEnum.Status404;
  }
  if (ServerError.is(networkError)) {
    if (networkError.statusCode === 401) {
      return ErrorPageStatusEnum.Status401;
    }
    if (networkError.statusCode === 503) {
      return ErrorPageStatusEnum.Status503;
    }
  }
  if (
    networkError instanceof TypeError &&
    networkError.message === 'Failed to fetch'
  ) {
    return ErrorPageStatusEnum.Status503;
  }
  // A request that timed out carries no status of its own: `customFetch`
  // rejects with a plain Error named FETCH_TIMEOUT_ERROR_NAME, and a
  // ServiceWorker that never answers its MessageChannel rejects with
  // SW_REQUEST_TIMEOUT_MESSAGE (see the user app's ApolloClient). Both mean the
  // server was never reached, so reporting them as an internal server error
  // blames the wrong side.
  //
  // Matched against those two producers specifically rather than by searching
  // the message for "timed out": a real backend fault whose body says so - a
  // 500 carrying "Query timed out" - reaches this line too, and calling that a
  // 503 is the same misattribution in reverse.
  if (
    networkError instanceof Error &&
    (networkError.name === FETCH_TIMEOUT_ERROR_NAME ||
      networkError.message.startsWith(SW_REQUEST_TIMEOUT_MESSAGE))
  ) {
    return ErrorPageStatusEnum.Status503;
  }
  return ErrorPageStatusEnum.Status500;
}
